import { ErrorBoundary } from '@dixa/lib-shared-habla';
import {
  Fragment,
  FunctionComponent,
  lazy,
  Suspense,
  useCallback,
  useEffect,
} from 'react';
import { useLocation } from 'react-use';

import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { loadMiurosMfeRemote, MiurosMfeRemoteId } from '../utils/loadRemote';

import { MiurosMfeError } from './miurosMfeError';
import { MiurosMfeLoading } from './miurosMfeLoading';

const MfeBiShell = (): JSX.Element => {
  const eventName = 'ng-bi:event';
  const location = useLocation();
  const dispatch = useAppDispatch();

  const handleEvent = useCallback(
    (event: Event) => {
      if (!event || !event['detail']) {
        return;
      }

      const detail = event['detail'];

      if (detail['eventType'] === 'conversationPreview' && !!detail['id']) {
        dispatch(
          appActions.showModal({
            modal: {
              type: 'app:conversationPreview',
              arg: { csid: detail['id'] },
            },
          }),
        );
      }
    },
    [dispatch],
  );

  useEffect(() => {
    document.addEventListener(eventName, handleEvent);

    return () => {
      document.removeEventListener(eventName, handleEvent);
    };
  }, []);

  return location.pathname ? (
    <ng-bi location={location.pathname + location.search}></ng-bi>
  ) : (
    <Fragment></Fragment>
  );
};

const MfeQaShell = (): JSX.Element => {
  const location = useLocation();

  return location.pathname ? (
    <ng-qa location={location.pathname + location.search}></ng-qa>
  ) : (
    <Fragment></Fragment>
  );
};

// MFE view factory
export const getMiurosMfeView = (
  remoteId: MiurosMfeRemoteId,
  rootUrl: string,
): FunctionComponent => {
  const LazyNgShell = lazy(async () => {
    const success = await loadMiurosMfeRemote(remoteId, rootUrl);
    return {
      default: success
        ? remoteId === 'bi'
          ? MfeBiShell
          : MfeQaShell
        : MiurosMfeError,
    };
  });

  const MfeView = (): JSX.Element => {
    return (
      <ErrorBoundary fallback={<MiurosMfeError />}>
        <Suspense fallback={<MiurosMfeLoading />}>
          <LazyNgShell></LazyNgShell>
        </Suspense>
      </ErrorBoundary>
    );
  };

  return MfeView;
};
